import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAdminAuth, formatApiErrorDetail } from "../../lib/admin-auth-context";
import { CheckCircle, XCircle, Link as LinkIcon, X } from "@phosphor-icons/react";

const formatDate = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(v);
  }
};

const STATUS_CLS = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

const StatusPill = ({ value }) => (
  <span
    className={`inline-flex items-center px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase font-bold border ${
      STATUS_CLS[value] || STATUS_CLS.pending
    }`}
    style={{ borderRadius: 4 }}
  >
    {value === "approved" ? "Aprobado" : value === "rejected" ? "Rechazado" : "Pendiente"}
  </span>
);

const ApproveDialog = ({ user, clients, onClose, onConfirm }) => {
  const [clientId, setClientId] = useState(user.linked_client_id || "");
  const [busy, setBusy] = useState(false);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      data-testid="admin-portal-approve-dialog"
    >
      <div
        className="bg-white w-full max-w-md"
        style={{ borderRadius: 4 }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="font-display text-xl font-semibold tracking-tight text-[var(--icor-navy)]">
            Aprobar y vincular
          </h2>
          <button onClick={onClose} className="text-slate-500" aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
              Usuario
            </div>
            <div className="font-medium text-[var(--icor-navy)] mt-1">
              {user.full_name}
            </div>
            <div className="text-sm text-slate-500">{user.email}</div>
            <div className="text-sm text-slate-500">{user.company}</div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2">
              Vincular a cliente ICOR
            </label>
            <select
              required
              className="icor-input"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              data-testid="admin-portal-approve-client"
            >
              <option value="">Selecciona un cliente…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-2">
              El cliente seleccionado se usará para mostrar pedidos, envíos y
              facturas al usuario.
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="btn-outline-dark">
              Cancelar
            </button>
            <button
              disabled={!clientId || busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await onConfirm(clientId);
                } finally {
                  setBusy(false);
                }
              }}
              className="btn-primary disabled:opacity-60"
              data-testid="admin-portal-approve-confirm"
            >
              {busy ? "Aprobando..." : "Aprobar acceso"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function AdminPortalUsers() {
  const { api } = useAdminAuth();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([
        api.get("/portal-users"),
        api.get("/clients"),
      ]);
      setUsers(Array.isArray(u.data) ? u.data : []);
      setClients(Array.isArray(c.data) ? c.data : []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const approve = async (user, clientId) => {
    try {
      await api.post(`/portal-users/${user.id}/approve`, { client_id: clientId });
      toast.success("Acceso aprobado");
      setApproving(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const reject = async (user) => {
    if (!window.confirm(`¿Rechazar el acceso de ${user.email}?`)) return;
    try {
      await api.post(`/portal-users/${user.id}/reject`);
      toast.success("Acceso rechazado");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (user) => {
    if (!window.confirm(`¿Eliminar definitivamente a ${user.email}?`)) return;
    try {
      await api.delete(`/portal-users/${user.id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

  return (
    <div data-testid="admin-portal-users-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs tracking-[0.24em] uppercase font-bold text-[var(--icor-blue)] eyebrow-line">
            Portal Cliente
          </p>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tighter mt-3 text-[var(--icor-navy)]">
            Usuarios del portal
          </h1>
          <p className="text-sm text-slate-500 mt-2 max-w-xl">
            Aprueba o rechaza solicitudes de acceso de clientes y vincúlalas con
            un registro de cliente existente.
          </p>
        </div>
      </div>

      <div
        className="bg-white border border-slate-200 overflow-x-auto"
        style={{ borderRadius: 4 }}
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {[
                "Nombre",
                "Empresa",
                "Correo",
                "Vinculado a",
                "Registrado",
                "Estado",
                "",
              ].map((h) => (
                <th
                  key={h}
                  className="text-left px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                  Cargando…
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                  Sin solicitudes.
                </td>
              </tr>
            ) : (
              users.map((u) => (
                <tr
                  key={u.id}
                  data-testid={`admin-portal-user-row-${u.id}`}
                  className="border-b border-slate-100 hover:bg-slate-50"
                >
                  <td className="px-4 py-3 text-slate-700 font-medium">
                    {u.full_name}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{u.company}</td>
                  <td className="px-4 py-3 text-slate-700">{u.email}</td>
                  <td className="px-4 py-3 text-slate-700">
                    {u.linked_client_id ? clientName(u.linked_client_id) : "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-500">
                    {formatDate(u.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill value={u.status} />
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => setApproving(u)}
                      className="p-1.5 text-emerald-600 hover:bg-emerald-50"
                      title="Aprobar / vincular"
                      data-testid={`admin-portal-user-approve-${u.id}`}
                    >
                      {u.linked_client_id ? (
                        <LinkIcon size={16} />
                      ) : (
                        <CheckCircle size={16} />
                      )}
                    </button>
                    <button
                      onClick={() => reject(u)}
                      className="p-1.5 text-amber-600 hover:bg-amber-50 ml-1"
                      title="Rechazar"
                      data-testid={`admin-portal-user-reject-${u.id}`}
                    >
                      <XCircle size={16} />
                    </button>
                    <button
                      onClick={() => remove(u)}
                      className="p-1.5 text-red-600 hover:bg-red-50 ml-1"
                      title="Eliminar"
                      data-testid={`admin-portal-user-delete-${u.id}`}
                    >
                      <X size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {approving && (
        <ApproveDialog
          user={approving}
          clients={clients}
          onClose={() => setApproving(null)}
          onConfirm={(cid) => approve(approving, cid)}
        />
      )}
    </div>
  );
}
